import React, { useState, useEffect } from 'react'
import CalendarHeatmap from 'react-calendar-heatmap'
import 'react-calendar-heatmap/dist/styles.css'
import { Tooltip } from 'react-tooltip'
import { FaChevronLeft, FaChevronRight, FaFilter } from 'react-icons/fa'
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval
} from 'date-fns'
import { fr } from 'date-fns/locale'
import { getAllUserSessions } from '../../utils/analyticsUtils'
import './UsageCalendarHeatmap.css'

const MONTHS_PER_PAGE = 4
const CURRENT_YEAR = new Date().getFullYear()
const INIT_PAGE = Math.floor(new Date().getMonth() / MONTHS_PER_PAGE)

export default function UsageCalendarHeatmap() {
  const [year, setYear] = useState(CURRENT_YEAR)
  const [page, setPage] = useState(INIT_PAGE)
  const [data, setData] = useState([])
  const [maxCount, setMaxCount] = useState(0)
  const [viewMode, setViewMode] = useState('total') // 'total', 'activity', 'inactivity'

  // Charger les données à chaque changement d'année ou de mode de vue
  useEffect(() => {
    buildDataForYear(year)
    if (year === CURRENT_YEAR) setPage(INIT_PAGE)
    else setPage(0)
  }, [year, viewMode])

  function buildDataForYear(y) {
    const sessions = getAllUserSessions()
    const start = startOfYear(new Date(y, 0, 1))
    const end = endOfYear(new Date(y, 11, 31))
    const daily = {}
    
    eachDayOfInterval({ start, end }).forEach(d => {
      daily[format(d, 'yyyy-MM-dd')] = {
        total: 0,
        activity: 0,
        inactivity: 0
      }
    })
    
    sessions.forEach(s => {
      if (!s.startedAt || !s.endedAt) return
      const dayKey = format(parseISO(s.startedAt), 'yyyy-MM-dd')
      if (daily.hasOwnProperty(dayKey)) {
        // Durée totale de la session
        const totalDur = (parseISO(s.endedAt).getTime() - parseISO(s.startedAt).getTime()) / (60 * 1000)
        daily[dayKey].total += totalDur
        
        // Temps d'activité et d'inactivité
        daily[dayKey].activity += s.activity_duration || 0
        daily[dayKey].inactivity += s.inactivity_duration || 0
      }
    })
    
    // Convertir en format attendu selon le mode de vue
    const arr = Object.entries(daily).map(([date, counts]) => ({
      date,
      count: Math.round(counts[viewMode]),
      total: Math.round(counts.total),
      activity: Math.round(counts.activity),
      inactivity: Math.round(counts.inactivity)
    }))
    
    setData(arr)
    setMaxCount(Math.max(...arr.map(v => v.count)))
  }

  // Classe de couleur pour chaque valeur
  const classForValue = v => {
    if (!v || v.count === 0) return 'color-empty'
    const norm = v.count / maxCount
    if (norm <= 0.2) return 'color-scale-1'
    if (norm <= 0.4) return 'color-scale-2'
    if (norm <= 0.6) return 'color-scale-3'
    if (norm <= 0.8) return 'color-scale-4'
    return 'color-scale-5'
  }

  // Tooltip personnalisé avec informations filtrées
  const tooltipAttrs = v => {
    if (!v || !v.date)
      return {
        'data-tooltip-id': 'cal-tip',
        'data-tooltip-content': 'Aucune utilisation'
      }
    
    const d = parseISO(v.date)
    const label = format(d, 'EEEE d MMMM yyyy', { locale: fr })
    
    const formatTime = (minutes) => {
      if (minutes === 0) return '0min'
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min`
    }
    
    // CORRECTION: Afficher seulement la valeur filtrée selon le mode
    let content = `${label}\n`
    
    switch(viewMode) {
      case 'activity':
        content += `Activité: ${formatTime(v.activity)}`
        break
      case 'inactivity':
        content += `Inactivité: ${formatTime(v.inactivity)}`
        break
      case 'total':
      default:
        content += `Temps total: ${formatTime(v.total)}`
        break
    }
    
    return {
      'data-tooltip-id': 'cal-tip',
      'data-tooltip-content': content
    }
  }

  // Obtenir le titre selon le mode de vue
  const getViewModeTitle = () => {
    switch(viewMode) {
      case 'activity':
        return "Temps d'activité"
      case 'inactivity':
        return "Temps d'inactivité"
      case 'total':
      default:
        return "Temps total d'utilisation"
    }
  }

  // Pagination des mois
  const allMonths = Array.from({ length: 12 }, (_, i) => i)
  const totalPages = Math.ceil(allMonths.length / MONTHS_PER_PAGE)
  const visible = allMonths.slice(
    page * MONTHS_PER_PAGE,
    (page + 1) * MONTHS_PER_PAGE
  )

  return (
    <div className="usage-calendar">
      {/* Header avec sélecteur d'année et filtre */}
      <div className="calendar-header">
        <h3>{getViewModeTitle()}</h3>
        <div className="calendar-controls">
          <button
            onClick={() => setYear(y => Math.max(y - 1, 1970))}
            className="nav-button"
          >
            <FaChevronLeft />
          </button>
          <span className="year-display">{year}</span>
          <button
            onClick={() => setYear(y => Math.min(y + 1, CURRENT_YEAR))}
            className="nav-button"
            disabled={year === CURRENT_YEAR}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* Filtre de mode de vue */}
      <div className="view-mode-filter">
        <div className="filter-label">
          <FaFilter className="filter-icon" />
          <span>Affichage :</span>
        </div>
        <div className="filter-options">
          <button
            className={`filter-option ${viewMode === 'total' ? 'active' : ''}`}
            onClick={() => setViewMode('total')}
          >
            Temps total
          </button>
          <button
            className={`filter-option ${viewMode === 'activity' ? 'active' : ''}`}
            onClick={() => setViewMode('activity')}
          >
            Activité
          </button>
          <button
            className={`filter-option ${viewMode === 'inactivity' ? 'active' : ''}`}
            onClick={() => setViewMode('inactivity')}
          >
            Inactivité
          </button>
        </div>
      </div>

      {/* Navigation mensuelle */}
      <div className="month-navigation">
        <button
          onClick={() => setPage(p => Math.max(p - 1, 0))}
          disabled={page === 0}
          className="nav-month-button"
        >
          <FaChevronLeft />
        </button>
        <div className="month-container">
          {visible.map(m => {
            const start = startOfMonth(new Date(year, m, 1))
            const end = endOfMonth(start)
            const monthDates = eachDayOfInterval({ start, end })
            const monthValues = monthDates.map(d => {
              const dateStr = format(d, 'yyyy-MM-dd')
              const found = data.find(entry => entry.date === dateStr)
              return found || { 
                date: dateStr, 
                count: 0, 
                total: 0, 
                activity: 0, 
                inactivity: 0 
              }
            })
            return (
              <div key={m} className="heatmap-month-box">
                <h4>{format(start, 'MMM', { locale: fr }).toUpperCase()}</h4>
                <CalendarHeatmap
                  startDate={start}
                  endDate={end}
                  values={monthValues}
                  classForValue={classForValue}
                  tooltipDataAttrs={tooltipAttrs}
                  showMonthLabels={false}
                  showWeekdayLabels={false}
                  gutterSize={4}
                  showOutOfRangeDays={true}
                />
              </div>
            )
          })}
        </div>
        <button
          onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
          disabled={page === totalPages - 1}
          className="nav-month-button"
        >
          <FaChevronRight />
        </button>
      </div>

      <Tooltip id="cal-tip" />

      {/* Légende mise à jour */}
      <div className="legend-details">
        <div className="legend-title">Guide des couleurs - {getViewModeTitle()}</div>
        <div className="legend-items">
          {[
            ['Aucune utilisation', 'color-empty'],
            ['Faible', 'color-scale-1'],
            ['Modérée', 'color-scale-2'],
            ['Élevée', 'color-scale-3'],
            ['Très élevée', 'color-scale-4'],
            ['Maximale', 'color-scale-5']
          ].map(([label, cls]) => (
            <div key={cls} className="legend-detail-item">
              <div className={`legend-color ${cls}`} />
              <span className="legend-text">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}